import {
  faBookOpen,
  faBuilding,
  faClipboardCheck,
  faGraduationCap,
  faHouse,
  faLineChart,
  faMountain,
  faRoute,
  faShirt,
} from '@fortawesome/pro-light-svg-icons';
import type {
  NavChild,
  NavEntry,
  PanelSpec,
} from '@/components/shared/Sidebar/types';
import type { useFeatures } from '@/hooks/useFeatures';
import { PLATFORM_FEATURES } from '@/lib/constants/features';
import type { AllPermissions } from '@/lib/constants/permissions';

/** True when `pathname` is `prefix` itself or a route nested under it. */
export function isPathActive(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Every rail entry that owns a second level. Groups contribute their visible
 * children; a single-page item (`panelLabel` set) contributes a one-entry panel
 * so the expanded sidebar still shows a second level on Dashboard and Network
 * instead of going blank. External-link items are skipped — they have no page.
 */
export function buildPanelSpecs(nav: NavEntry[]): PanelSpec[] {
  const specs: PanelSpec[] = [];
  for (const entry of nav) {
    if (entry.type === 'group') {
      const children = entry.children.filter(c => !c.visible || c.visible());
      if (children.length > 0)
        specs.push({ key: entry.key, label: entry.label, children });
    } else if (entry.type === 'item' && entry.href && entry.panelLabel) {
      specs.push({
        key: entry.key,
        label: entry.panelLabel,
        children: [
          { key: entry.key, label: entry.panelLabel, href: entry.href },
        ],
      });
    }
  }
  return specs;
}

/**
 * The one child the current route belongs to — the longest matching href wins.
 *
 * Entries nest: `/management/platform-features/$featureId` sits under
 * `/management/platform-features`, so a plain prefix test lights up both and
 * the panel shows two selected rows for one page. Longest match puts the
 * highlight on the page actually open.
 */
export function activeChildKey(
  children: NavChild[],
  pathname: string,
): string | undefined {
  let best: NavChild | undefined;
  let bestLength = -1;
  for (const child of children) {
    const prefix = child.matchPrefix ?? child.href;
    if (!isPathActive(pathname, prefix)) continue;
    if (prefix.length > bestLength) {
      best = child;
      bestLength = prefix.length;
    }
  }
  return best?.key;
}

/**
 * Unfold `key` in the expanded column's open-group list.
 *
 * Returns the list untouched when the group is already unfolded. The Sidebar
 * unfolds the group owning the current route on every navigation; handing back
 * a fresh array each time would change the effect's dependency on every run and
 * loop, so identity stability here is load-bearing, not an optimisation.
 */
export function withGroupOpen(open: string[], key: string): string[] {
  return open.includes(key) ? open : [...open, key];
}

/** Fold an unfolded group, unfold a folded one; the rest are left alone. */
export function withGroupToggled(open: string[], key: string): string[] {
  return open.includes(key) ? open.filter(k => k !== key) : [...open, key];
}

/** True when the current route belongs to this panel's owner. */
export function isSpecActive(spec: PanelSpec, pathname: string): boolean {
  // `/` renders the dashboard, so it counts as that spec being active.
  if (spec.key === 'dashboard' && pathname === '/') return true;
  return spec.children.some(c =>
    isPathActive(pathname, c.matchPrefix ?? c.href),
  );
}

/** The resources the nav gates on, resolved by the caller. */
export interface NavPermissions {
  orders: AllPermissions['orders'];
  automationRules: AllPermissions['automation-rules'];
  risk: AllPermissions['risk'];
  productMapping: AllPermissions['product-mapping'];
  supplyChainMapping: AllPermissions['supply-chain-mapping'];
  /** Note the singular: the resource is `constant-mapping`, the feature `constants-mapping`. */
  constantMapping: AllPermissions['constant-mapping'];
  apiKey: AllPermissions['api-key'];
  entityScreening: AllPermissions['entity-screening'];
  reports: AllPermissions['reports'];
  styles: AllPermissions['styles'];
  network: AllPermissions['network'];
  questionnaires: AllPermissions['questionnaires'];
  documents: AllPermissions['documents'];
  certificates: AllPermissions['certificates'];
  capa: AllPermissions['capa'];
  audit: AllPermissions['audit'];
  slcp: AllPermissions['slcp'];
  fiberForward: AllPermissions['fiber-forward'];
  files: AllPermissions['files'];
  certifiedMaterials: AllPermissions['certified-materials'];
}

export function buildNav(
  f: ReturnType<typeof useFeatures>,
  t: (key: string) => string,
  canAccessEntityScreening: boolean,
  perms: NavPermissions,
): NavEntry[] {
  const traceabilityChildren: NavChild[] = [
    {
      key: 'chain-mapping',
      label: t('supplyChainMapping'),
      href: '/supply-chain-mapping',
      visible: () =>
        f.isFeatureEnabled('supply-chain-mapping') &&
        perms.supplyChainMapping.canRead,
    },
    {
      key: 'product-mapping',
      label: t('productMapping'),
      href: '/product-mapping',
      // The feature says the company bought product mapping; the permission
      // says this user may look at it. A reader holds `read` and still sees
      // the entry — it is `read` that opens the page, not a write.
      visible: () => f.isProductMappingEnabled && perms.productMapping.canRead,
    },
    {
      key: 'orders',
      // With the Order Hub, tracing orders are "Tracing Requests".
      label: t(f.isOrdersHubEnabled ? 'orders-hub' : 'orders'),
      href: '/orders',
      // Same pair as every other entry: the feature says the company bought
      // Cascade Tracing, `read` says this user may look at it.
      visible: () => f.isCascadeTracingEnabled && perms.orders.canRead,
    },
    {
      key: 'orders-hub',
      label: t('ordersHub'),
      href: '/orders-hub',
      visible: () =>
        f.isOrdersHubEnabled &&
        f.isCascadeTracingEnabled &&
        perms.orders.canRead,
    },
    {
      key: 'automation-rules',
      label: t('automationRules'),
      href: '/automation-rules',
      visible: () =>
        f.isFeatureEnabled(PLATFORM_FEATURES.AUTOMATION_RULES) &&
        perms.automationRules.canRead,
    },
    {
      key: 'entity-screening-v2',
      label: t('entityScreeningV2'),
      href: '/entity-screening',
      visible: () =>
        f.isEntityScreeningV2Enabled &&
        canAccessEntityScreening &&
        perms.entityScreening.canRead,
    },
    {
      key: 'certified-materials',
      label: t('certifiedMaterials'),
      href: '/certified-materials',
      // Either flag says the company bought the module (and which side of it),
      // `certified-materials:read` says this user may look.
      visible: () =>
        (f.isFeatureEnabled('certified-materials-brand') ||
          f.isFeatureEnabled('certified-materials-supplier')) &&
        perms.certifiedMaterials.canRead,
    },
    {
      key: 'fiber-transactions',
      label: t('transactions'),
      href: '/transactions',
      // One feature and one resource cover all three entries: Fiber Forward is
      // sold as a unit, and transactions, productions and inventory are three
      // views of the same lot ledger rather than three things to grant.
      visible: () =>
        f.isFeatureEnabled('fiber-forward') && perms.fiberForward.canRead,
    },
    {
      key: 'fiber-productions',
      label: t('productions'),
      href: '/productions',
      visible: () =>
        f.isFeatureEnabled('fiber-forward') && perms.fiberForward.canRead,
    },
    {
      key: 'fiber-inventory',
      label: t('inventory'),
      href: '/inventory/products',
      matchPrefix: '/inventory',
      visible: () =>
        f.isFeatureEnabled('fiber-forward') && perms.fiberForward.canRead,
    },
    {
      key: 'files-management',
      label: t('filesManagement'),
      href: '/files',
      // Two features and a grant: Cascade Tracing plus the document-management
      // add-on say the company bought it, `files:read` says this user may look.
      visible: () =>
        f.isCascadeTracingEnabled &&
        f.isFeatureEnabled('tracing-document-management') &&
        perms.files.canRead,
    },
  ];

  const canReadReports = perms.reports.canRead;
  const reportChildren: NavChild[] = [
    {
      key: 'rpt-tracing-ops',
      label: t('tracingOperations'),
      href: '/report/tracing-operations',
      visible: () => f.isCascadeTracingEnabled && canReadReports,
    },
    {
      key: 'rpt-tracing-comp',
      label: t('tracingCompliance'),
      href: '/report/tracing-compliance',
      visible: () => f.isCascadeTracingEnabled && canReadReports,
    },
    {
      key: 'rpt-cert-comp',
      label: t('certificateCompliance'),
      href: '/report/certificate-compliance',
      visible: () => canReadReports,
    },
    {
      key: 'rpt-audit-comp',
      label: t('auditCompliance'),
      href: '/report/audit-compliance',
      visible: () => canReadReports,
    },
  ];

  return [
    {
      type: 'item',
      key: 'dashboard',
      label: t('home'),
      icon: faHouse,
      href: '/dashboard',
      panelLabel: t('dashboard'),
    },
    ...(perms.network.canRead
      ? [
          {
            type: 'item' as const,
            key: 'network',
            label: t('network'),
            icon: faBuilding,
            href: '/network',
            panelLabel: t('network'),
          },
        ]
      : []),
    { type: 'section', label: t('sectionCatalog') },
    {
      type: 'group',
      key: 'products',
      label: t('products'),
      icon: faShirt,
      children: [
        {
          key: 'my-styles',
          label: t('myStyles'),
          href: '/my-styles',
          visible: () => perms.styles.canRead,
        },
        {
          key: 'my-components',
          label: t('myComponents'),
          href: '/my-components',
          visible: () => perms.styles.canRead,
        },
      ],
    },
    {
      type: 'group',
      key: 'compliance',
      label: t('vendorCompliance'),
      icon: faClipboardCheck,
      children: [
        {
          key: 'questionnaires',
          label: t('questionnaires'),
          href: '/questionnaires',
          visible: () => perms.questionnaires.canRead,
        },
        {
          key: 'documents',
          label: t('documents'),
          href: '/documents',
          visible: () => perms.documents.canRead,
        },
        {
          key: 'certifications',
          label: t('certifications'),
          href: '/certifications',
          visible: () => perms.certificates.canRead,
        },
        {
          key: 'corrective-actions',
          label: t('capa'),
          href: '/capa',
          visible: () => f.isCapaV2Enabled && perms.capa.canRead,
        },
        {
          key: 'audits',
          label: t('audits'),
          href: '/audits',
          visible: () => perms.audit.canRead,
        },
        {
          key: 'slcps',
          label: t('slcps'),
          href: '/slcp/my-own/active',
          matchPrefix: '/slcp',
          visible: () => perms.slcp.canRead,
        },
      ],
    },
    {
      type: 'group',
      key: 'traceability',
      label: t('traceability'),
      shortLabel: t('traceabilityShort'),
      icon: faRoute,
      children: traceabilityChildren,
    },
    {
      type: 'group',
      key: 'risk',
      label: t('risk'),
      icon: faMountain,
      children: [
        {
          key: 'risk-landscape',
          label: t('riskLandscape'),
          href: '/risk/landscape',
          visible: () => f.isRiskAnalysisV3Enabled && perms.risk.canRead,
        },
        {
          key: 'rules',
          label: t('rules'),
          href: '/risk/rules',
          visible: () => f.isRiskAnalysisV3Enabled && perms.risk.canRead,
        },
        {
          key: 'assessments',
          label: t('assessments'),
          href: '/risk/assessments',
          visible: () => f.isRiskAnalysisV3Enabled && perms.risk.canRead,
        },
      ],
    },
    {
      type: 'group',
      key: 'reports',
      label: t('reports'),
      icon: faLineChart,
      children: reportChildren,
    },
    { type: 'section', label: t('sectionResources') },
    // The "Developers' HQ" group that used to live here is gone — API Keys
    // and Constants Mapping, its only two children, both moved to Company
    // Settings (`/company-settings/api-keys`, `/company-settings/
    // constants-mapping`): tenant administration the caller's own company
    // owns, same family as Users and Permission sets, which were already
    // there. Nothing in this "Resources" section is company-scoped anymore.
    {
      type: 'item',
      key: 'help-center',
      label: t('helpCenter'),
      shortLabel: t('helpCenterShort'),
      icon: faBookOpen,
      onClick: () => window.open('https://help.retraced.com/', '_blank'),
    },
    {
      type: 'item',
      key: 'hrdd-academy',
      label: t('hrdd'),
      icon: faGraduationCap,
      onClick: () => window.open('https://www.hrddacademy.com/', '_blank'),
      accent: true,
    },
  ];
}
